import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAppDevelopersQuery,
  useSimilarAppsQuery,
} from "@/features/auth/api/authAPI";
import {
  additionlInfoModalActions,
  getAdditionInfoModal, // Assuming this selector returns the whole slice: { isOpen, isModalOpen }
} from "@/entities/additionInfoModal";
import AdditionalInfoModalUI from "@/shared/ui/AdditionalInfoModal/AdditionalInfoModal"; // Renamed to avoid confusion
import {
  formatCategoryName,
  formatDate,
  formatNumber,
} from "@/shared/lib/constants/formaters";
// import { AdditionalInfoProps } from "../../types/additionalInfoProps"; // Already defined below
import styles from "./AdditionalInfo.module.scss";
import SaveApp from "@/shared/ui/SaveApp/SaveApp";
import { handleOpenVideoPage } from "@/app/libs/const/handleOpenVideoPage";
import { XIcon } from "lucide-react";

interface AdditionalInfoProps {
  data: any;
  handleSaveApp?: () => void;
  isStarred?: boolean;
  isHidden?: boolean;
  removeStarredApp?: () => void;
  setTempText?: (text: string) => void;
  appId?: number;
  hiddenAppItemId?: number;
}

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({
  data,
  handleSaveApp,
  isStarred,
  isHidden,
  removeStarredApp,
  setTempText,
  appId,
  hiddenAppItemId,
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Add null check for data and data.developer
  const { data: appDevelopers } = useGetAppDevelopersQuery(
    data?.developer?.id || 0,
    { skip: !data?.developer?.id },
  );

  // Remove skip condition for SEO - always load similar apps for crawlers
  const { data: similarApps } = useSimilarAppsQuery(
    { app: appId || 0 }
    // Removed skip condition so crawlers can always see similar apps
  );

  // Get BOTH state values. 'isOpen' controls this main panel's visibility.
  // 'isModalOpen' controls the nested developer info pop-up.
  const { isOpen: isPanelOpen, isModalOpen: isDeveloperPopoverOpen } =
    useSelector(getAdditionInfoModal);
  const dispatch = useDispatch();

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      dispatch(additionlInfoModalActions.closeItem()); // This sets isOpen to false
      // dispatch(additionlInfoModalActions.closeModal()); // This closes the developer popover
    }
  };

  const handleMouseEnterDeveloper = () => {
    dispatch(additionlInfoModalActions.openModal()); // Opens developer popover
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const handleMouseLeaveDeveloper = () => {
    timerRef.current = setTimeout(() => {
      dispatch(additionlInfoModalActions.closeModal()); // Closes developer popover
    }, 400);
  };

  const handleMouseEnterModal = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const handleMouseLeaveModal = () => {
    timerRef.current = setTimeout(() => {
      dispatch(additionlInfoModalActions.closeModal()); // Closes developer popover
    }, 400);
  };

  const handleClickDeveloper = (idApp: number) => {
    const appDeveloper = appDevelopers?.results.find(
      (item) => item.id === idApp,
    );
    if (appDeveloper) {
      // Added a check
      handleOpenVideoPage(
        0,
        appDeveloper.slug,
        "Developer",
        1,
        appDeveloper.id,
        0,
        undefined,
      );
    }
  };

  React.useEffect(() => {
    // Only add listener if the panel is supposed to be open
    if (isPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (timerRef.current) {
        clearTimeout(timerRef.current); // Clear timer on unmount
      }
    };
  }, [isPanelOpen]); // Rerun effect if isPanelOpen changes

  // If data is undefined, render nothing or a fallback UI
  if (!data) {
    return null;
  }

  // The root div now gets a conditional class
  return (
    <div
      className={`${styles.modal} ${!isPanelOpen ? styles.modalHidden : ""}`}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.closeButton}>
          <button
            onClick={() => dispatch(additionlInfoModalActions.closeItem())} // This sets isOpen to false
          >
            <XIcon />
          </button>
        </div>

        <div className={styles.containerTitle}>
          <div className={styles.containerTitleBox}>
            <img
              className={styles.logo}
              src={
                !data.icon || data.icon === ""
                  ? "/img/skeletonLogo.svg"
                  : data.icon
              }
              alt={`${data.name || "App"} logo icon`}
              loading="lazy"
              draggable={false}
            />
            <div className={styles.title}>
              <h1>{data.name || "Unnamed App"}</h1>
              <p>{data.shortname || ""}</p>
            </div>
            <SaveApp
              onClick={handleSaveApp || (() => {})}
              isStarred={isStarred || false}
              isHidden={isHidden || false}
              removeStarredApp={removeStarredApp || (() => {})}
              setTempText={setTempText || (() => {})}
              appId={appId || 0}
              hiddenAppItemId={hiddenAppItemId}
            />
          </div>
        </div>
        <div className={styles.line} />
        <div className={styles.containerInfo}>
          <div className={styles.containerBlockWrapper}>
            <div className={`${styles.containerBlock} ${styles.widthBlock}`}>
              <div className={styles.containerBlockTitle}>
                <h3>Developer</h3>
              </div>
              <button
                onMouseEnter={handleMouseEnterDeveloper}
                onMouseLeave={handleMouseLeaveDeveloper}
              >
                {!data.developer?.name ? "Unknown" : data.developer.name}
              </button>
            </div>
            <div className={styles.containerBlock}>
              <div className={styles.containerBlockTitle}>
                <h3>App Store</h3>
              </div>
              <a
                href={data.appstore_link || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.appStoreLink}
              >
                View in App Store
              </a>
            </div>
          </div>
          {data.categories && data.categories.length > 0 && (
            <div className={styles.containerBlock}>
              <div className={styles.containerBlockTitle}>
                <h3>Category</h3>
              </div>
              <p>
                {!data.categories ||
                data.categories.length === 0 ||
                !data.categories[0].name
                  ? "Unknown"
                  : formatCategoryName(data.categories[0].name)}
              </p>
            </div>
          )}
          <div className={styles.containerBlockWrapper}>
            <div className={`${styles.containerBlock} ${styles.widthBlock}`}>
              <div className={styles.containerBlockTitle}>
                <h3>Monthly Downloads</h3>
              </div>
              <p>
                {data.downloads === null ||
                data.downloads === 0 ||
                (data.downloads > 0 && data.downloads < 5000)
                  ? "<5k"
                  : formatNumber(data.downloads)}
              </p>
            </div>
            <div className={styles.containerBlock}>
              <div className={styles.containerBlockTitle}>
                <h3>Monthly Revenue</h3>
              </div>
              <p>
                {data.revenue === null ||
                data.revenue === 0 ||
                (data.revenue > 0 && data.revenue < 5000)
                  ? "<5k"
                  : `${formatNumber(data.revenue)}`}
              </p>
            </div>
          </div>
          <div className={styles.containerBlockWrapper}>
            <div className={`${styles.containerBlock} ${styles.widthBlock}`}>
              <div className={styles.containerBlockTitle}>
                <h3>Release date</h3>
              </div>
              <p>
                {data.released === null ? "Unknown" : formatDate(data.released)}
              </p>
            </div>
            <div className={styles.containerBlock}>
              <div className={styles.containerBlockTitle}>
                <h3>Last Update</h3>
              </div>
              <p>
                {data.updated === "" ? "Unknown" : formatDate(data.updated)}
              </p>
            </div>
          </div>
          <div className={styles.containerBlockWrapper}>
            <div className={`${styles.containerBlock} ${styles.widthBlock}`}>
              <div className={styles.containerBlockTitle}>
                <h3>Onboarding Steps</h3>
              </div>
              <p>{!data.avs ? "Unknown" : data.avs.onboarding_step_count}</p>
            </div>
            <div className={styles.containerBlock}>
              <div className={styles.containerBlockTitle}>
                <h3>Paywall Type</h3>
              </div>
              <p>{!data.avs ? "Unknown" : data.avs.paywall_type}</p>
            </div>
          </div>
          <div className={styles.containerBlock}>
            <div className={styles.containerBlockTitle}>
              <h3>Running ads</h3>
            </div>
            <p>
              {!data.avs
                ? "Unknown"
                : data.avs.has_onboarding_with_quiz
                  ? "Yes"
                  : "No"}
            </p>
          </div>
        </div>
        <div className={styles.line} />
        <div className={styles.containerInfo}>
          <div className={styles.containerBlock}>
            <div className={styles.containerBlockTitle}>
              <h3>Similar Apps</h3>
            </div>
          </div>
          <div className={styles.similarApps}>
            {similarApps?.results.slice(0, 12).map((app) => (
              <a
                key={app.id}
                href={`/apps/${app.slug}/?ts=0&vt=1&id=${app.id}&av=${app.latest_appvideo_id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenVideoPage(
                    0,
                    app.slug,
                    "Similar App",
                    1,
                    app.id,
                    app.latest_appvideo_id,
                    undefined,
                  );
                }}
              >
                <img
                  src={app.icon || "/img/skeletonLogo.svg"}
                  alt={`${app.name} logo icon`}
                  title={app.name}
                  loading="eager"
                />
              </a>
            ))}
          </div>
        </div>
        <div className={styles.modalDeveloper}>
          {/* This is the nested popover, controlled by isDeveloperPopoverOpen */}
          {isDeveloperPopoverOpen && appDevelopers && (
            <AdditionalInfoModalUI // Using the renamed import
              onMouseEnter={handleMouseEnterModal}
              onMouseLeave={handleMouseLeaveModal}
              onClick={handleClickDeveloper}
              data={appDevelopers}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdditionalInfo;
